import {
  Button,
  Cluster,
  CopyButton,
  Field,
  Icon,
  Input,
  Panel,
  Thinking,
  Title,
} from "@fwf/fwfa-design-system/react";
import type { AuthChallenge } from "@/types";

interface Props {
  challenge: AuthChallenge;
}

export function TerminalAuthCard({ challenge }: Props) {
  const hasBrowserUrl = Boolean(challenge.url?.trim());

  return (
    <div className="animate-scale-in fixed right-4 top-4 z-50 w-72">
      {/* Panel is transparent on dark (--fwfa-card-bg), which is right for a panel
          sitting on the page and wrong for one floating over it. The opaque twin plus
          the float shadow are the DS recipe for a card that covers content. */}
      <Panel className="flex flex-col gap-4 bg-surface-opaque shadow-float">
        <Cluster className="gap-2">
          {/* Bare Icon, not framed: the DS reserves the framed tile for specimens, and
              a tinted chip here would spend an accent the card has no budget for. */}
          <Icon aria-hidden="true">
            <svg
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
          </Icon>
          <Title as="h2" className="fwfa-title--component">
            Sign in required
          </Title>
        </Cluster>

        {challenge.code && (
          <div className="flex flex-col gap-2">
            <Field label="Your device code">
              {/* Read-only rather than static text: the field skin, the tap-height
                  floor, and select-on-focus all come free, and the value stays
                  selectable for anyone who would rather drag than press Copy. */}
              <Input
                readOnly
                value={challenge.code}
                onFocus={(event) => event.currentTarget.select()}
                className="text-center font-mono font-semibold tracking-widest"
              />
            </Field>
            {/* Secondary while there is a browser button to be primary; the sole
                action otherwise. One primary per view, and no accent in either case. */}
            <CopyButton
              text={challenge.code}
              variant={hasBrowserUrl ? "secondary" : "primary"}
              className="w-full"
            >
              Copy code
            </CopyButton>
          </div>
        )}

        {hasBrowserUrl && (
          <Button
            variant="primary"
            className="w-full"
            onClick={() => window.freshvibe.openExternal(challenge.url)}
            leftIcon={
              <Icon aria-hidden="true">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </Icon>
            }
          >
            Open browser
          </Button>
        )}

        <Cluster className="gap-2 text-xs text-ink-muted">
          {/* Thinking carries the live region; the visible copy repeats it, so it is
              hidden from assistive tech to avoid announcing the same thing twice. */}
          <Thinking label="Waiting for you to finish signing in" />
          <span aria-hidden="true">Waiting for sign-in…</span>
        </Cluster>
      </Panel>
    </div>
  );
}
